import toast from 'react-hot-toast';
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareReviewModal = ({ show, onClose, onShared }) => {
  const [loading, setLoading] = useState(false);

  const handleShare = async () => {
    setLoading(true);

    try {
      await axios.post(
        `${API}/reviews`,
        {
          show_id: show.id,
          show_title: show.title,
          rating: show.personal_rating,
          review_text: show.personal_review
        },
        { withCredentials: true }
      );

      toast.success('Review shared successfully!');
      onShared();
    } catch (error) {
      console.error('Error sharing review:', error);
      toast.error('Error sharing review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="share-review-modal" data-testid="share-review-modal" aria-describedby={undefined}>
        <DialogHeader className="modal-header">
          <DialogTitle>Share Your Review</DialogTitle>
        </DialogHeader>

        <div className="modal-content">
          <div className="review-preview">
            <h3 data-testid="share-show-title">{show.title}</h3>
            <div className="rating" data-testid="share-rating">
              <span className="rating-badge">⭐ {show.personal_rating}/10</span>
            </div>
            <p className="review-text" data-testid="share-review-text">{show.personal_review}</p>
          </div>

          <p className="share-note">
            This will post your review to your feed so your friends can see it.
          </p>
        </div>

        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose} data-testid="share-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleShare} disabled={loading} data-testid="share-submit-btn">
            {loading ? 'Sharing...' : 'Share Review'}
          </Button>
        </div>

        <style jsx>{`
          .share-review-modal {
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            background: #1a1a1a !important;
            color: #ffffff !important;
            border: 1px solid #333333 !important;
          }

          .share-review-modal :global(button[class*="absolute"]) {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .share-review-modal :global(button[class*="absolute"]:hover) {
            color: #ffffff !important;
            background: transparent !important;
          }

          .share-review-modal :global(button[class*="absolute"]:focus) {
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }

          .modal-header {
            flex-shrink: 0;
            padding-bottom: 1rem;
            border-bottom: 1px solid #333333;
          }

          .modal-header :global(h2) {
            color: #ffffff !important;
          }

          .modal-content {
            flex: 1;
            overflow-y: auto;
            padding: 1rem 0;
            -webkit-overflow-scrolling: touch;
          }

          .review-preview {
            padding: 1.5rem;
            background: #2a2a2a;
            border: 1px solid #333333;
            border-radius: 12px;
            margin-bottom: 1rem;
          }

          .review-preview h3 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.75rem;
            word-break: break-word;
          }

          .rating {
            margin-bottom: 1rem;
          }

          .rating-badge {
            display: inline-block;
            padding: 0.35rem 0.85rem;
            background: #E62F88;
            color: #000000;
            border-radius: 20px;
            font-weight: 600;
            font-size: 0.95rem;
          }

          .review-text {
            color: #b0b0b0;
            line-height: 1.6;
            font-size: 1rem;
            word-break: break-word;
          }

          .share-note {
            color: #b0b0b0;
            font-size: 0.9rem;
            font-style: italic;
            padding: 0 0.5rem;
          }

          .modal-actions {
            display: flex;
            gap: 0.75rem;
            justify-content: flex-end;
            flex-shrink: 0;
            padding-top: 1rem;
            border-top: 1px solid #333333;
          }

          @media (max-width: 768px) {
            .share-review-modal {
              max-width: 100vw;
              max-height: 100vh;
              width: 100vw;
              height: 100vh;
              margin: 0;
              border-radius: 0;
              position: fixed;
              top: 0;
              left: 0;
              transform: none !important;
            }

            .modal-header {
              padding: 1rem 1rem 0.75rem;
            }

            .modal-content {
              padding: 1rem;
            }

            .review-preview {
              padding: 1rem;
            }

            .review-preview h3 {
              font-size: 1.25rem;
            }

            .review-text {
              font-size: 0.95rem;
            }

            .modal-actions {
              flex-direction: column-reverse;
              gap: 0.75rem;
              padding: 1rem;
            }

            .modal-actions button {
              width: 100%;
              min-height: 48px;
              font-size: 1rem;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ShareReviewModal;